import React from "react";
import { Form, FORM_END_PROCESSING } from "../../lib";
import { Doc, DocMeta, Tag } from "../../models";
import { navigate, eventEmitter, notification, i18n } from "../../services";
import { Map } from "immutable";
import styled from "styled-components";
import { fixArabicText } from "../../helpers/text";
import { connect } from "react-redux";
import { isUserAdmin } from "../../helpers";
import { Fields, Constraints } from "./types";
import { AxiosResponse } from "axios";
import { AppState } from "../../store";
import { RouteMatch } from "../../lib/routing/Navigator";

const Container = styled.div`
    margin: 0 auto 60px auto;
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
`;

const mapStateToProps = (state: AppState) => ({ user: state.user });
type StateProps = ReturnType<typeof mapStateToProps>;
type OwnProps = {
    doc_id: number;
    match: RouteMatch;
};
type Props = StateProps & OwnProps;
type State = {
    doc: {
        name: string;
    };
    doc_meta: Record<string, number[] | string>;
};

type DocMetaFields = {
    authors: string;
    intro: string;
    cover: string;
    tags: number[];
};
class DocMetaForm extends React.Component<Props, State> {
    private order = ["authors", "intro", "cover"];

    private fields: Fields = {
        authors: {
            type: "text",
            value: "",
        },
        intro: {
            type: "textarea",
            value: "",
        },
        cover: {
            type: "file",
            accept: [".png", "image/png", ".jpeg", "image/jpeg"],
        },
    };

    private constraints: Constraints = {
        authors: {
            presence: { message: "authorsRequired" },
            length: {
                minimum: 3,
                message: "authorsMinChars",
            },
        },
        intro: {
            presence: { message: "introRequired" },
            length: {
                minimum: 10,
                message: "introMinChars",
            },
        },
    };

    constructor(props: Props) {
        super(props);
        this.onSubmit = this.onSubmit.bind(this);

        if (isUserAdmin(this.props.user)) {
            this.order = ["authors", "tags", "intro", "cover"];
            this.fields.tags = {
                type: "pills",
                value: [],
                values: [],
                labels: [i18n.get("form.selectEmptyLabel")],
            };

            this.constraints.tags = {
                presence: { message: "tagsRequired" },
                length: {
                    minimum: 1,
                    maximum: 3,
                    message: "tagsCount_1_3",
                },
            };
        }

        this.state = {
            doc: {
                name: "",
            },
            doc_meta: {
                authors: "",
                tags: [],
                intro: "",
                cover: "",
            },
        };
    }

    async componentDidMount() {
        await this.fetch();
    }

    async fetch() {
        try {
            const { doc_id } = this.props;
            const {
                data: tags,
            }: AxiosResponse<
                Array<{
                    id: number;
                    name: string;
                }>
            > = await Tag.list();
            const { data: doc }: AxiosResponse<any> = await Doc.fetch(doc_id);
            const {
                data,
            }: AxiosResponse<
                Array<{
                    name: string;
                    value: string | number[];
                }>
            > = await DocMeta.list({ doc_id });

            let doc_meta = this.state.doc_meta;

            if (isUserAdmin(this.props.user)) {
                const values: number[] = [];
                const labels: string[] = [];
                tags.forEach((t) => {
                    values.push(t.id);
                    labels.push(t.name);
                });

                this.fields.tags.values = values;
                this.fields.tags.labels = labels;
            }

            this.fields = {
                ...this.fields,
            };

            for (const item of data) {
                let { name, value } = item;
                if (this.order.includes(name)) {
                    if (name === "cover") {
                        value = DocMeta.url("GET", "cover", { doc_id });
                    } else if (name === "tags" && typeof value === "string") {
                        value = value.split(",").map((v) => parseInt(v));
                    }

                    doc_meta[name] = value;
                }
            }

            this.setState({ doc, doc_meta });
        } catch (e) {
            console.warn("fetch", e);
        }
    }

    async onSubmit(values: Record<string, any>) {
        try {
            const { doc_id } = this.props;
            const { authors, intro, cover } = values;
            const data: Partial<DocMetaFields> = {
                authors,
                intro,
            };

            if (isUserAdmin(this.props.user)) {
                data.tags = values.tags;
            }

            await DocMeta.create(data, { doc_id });

            if (typeof cover !== "string") {
                const formData = new FormData();
                formData.append("file", cover);
                await DocMeta.upload(formData, { doc_id });
            }

            eventEmitter.emit(FORM_END_PROCESSING);
            notification.set("docMetaForm.saved", "s");

            await this.fetch();
        } catch (e) {
            console.warn(e);
        }
    }

    onCancel = () => navigate.to("/app/docs");

    render() {
        let description = undefined;
        if (this.state.doc !== null) {
            description = fixArabicText(this.state.doc!.name);
        }

        return (
            <Container>
                <Form
                    id="docMeta"
                    heading="title"
                    description={description}
                    order={this.order}
                    fields={this.fields}
                    constraints={this.constraints}
                    updateProp="doc_meta"
                    doc_meta={Map(this.state.doc_meta)}
                    focusOnFirst={true}
                    editing="Edit"
                    onSubmit={this.onSubmit}
                    onAlternate={this.onCancel}
                />
            </Container>
        );
    }
}

export default connect(mapStateToProps)(DocMetaForm);
